import { FC, ReactNode, createContext, useContext } from 'react';

export type InsightsPanelView = 'not_started' | 'recording' | 'summary';

/**
 * Analytics context value that can be provided by consumers to track
 * SDUI component interactions for aggregation purposes.
 */
export interface SDUIAnalyticsContextValue {
  /** Appointment ID for analytics context */
  appointmentId?: string;
  /** Current view derived from appointment status */
  view?: InsightsPanelView;
  /** Get the time since insights loaded, for time_since_loaded_ms tracking */
  getTimeSinceLoaded?: () => number | null;
  /** Called when an expandable section is expanded */
  onSectionExpanded?: () => void;
  /** Called when an expandable section is collapsed, with the time it was open */
  onSectionCollapsed?: (timeExpandedMs: number) => void;
  /** Register an expanded section for real-time tracking; returns unregister function */
  registerExpandedSection?: () => () => void;
  /** Called when a link is clicked */
  onLinkClicked?: () => void;
}

const SDUIAnalyticsContext = createContext<SDUIAnalyticsContextValue | null>(
  null,
);

export interface SDUIAnalyticsProviderProps {
  children: ReactNode;
  value: SDUIAnalyticsContextValue;
}

export const SDUIAnalyticsProvider: FC<SDUIAnalyticsProviderProps> = ({
  children,
  value,
}) => {
  return (
    <SDUIAnalyticsContext.Provider value={value}>
      {children}
    </SDUIAnalyticsContext.Provider>
  );
};

/**
 * Hook to access the optional SDUI analytics context.
 * Returns null if no provider is present.
 */
export const useSDUIAnalyticsContext = (): SDUIAnalyticsContextValue | null => {
  return useContext(SDUIAnalyticsContext);
};
